import {makeApiCall} from '../core';
import type { AccountContactReference, CustomerAccountCandidate, CustomerAccountOperation, CustomerAccountSummary } from '@ruta/shared';
import type {Customer, CustomerFormData, CustomerService, RateRequest,} from '../types';
import {CustomerSubscriptionsResponse, RateVisibility, ItemEnvelope, ListEnvelope, type CustomerAddressNeighbor, type ContactRole, type RelatedCustomer} from '@ruta/shared';
import type { ScheduleFrequency, CustomScheduleConfig, TermsAcceptance, PortalLogin, SubcontractorRateOverride, Period, PendingAccessRequest, AccessRequestDecisionType, SkipMakeupFlag, TemporaryNote } from '@ruta/shared';

// =====================
// Customer Management API
// =====================

export const getCustomerAccountCandidates = async (customerId: string, options: { contact?: AccountContactReference; accountId?: string }, cursor?: string) => {
  const params = new URLSearchParams(options.accountId ? { accountId: options.accountId } : {
    contactType: options.contact!.type, contactValue: options.contact!.value,
  });
  if (cursor) params.set('cursor', cursor);
  const res = await makeApiCall<ListEnvelope<CustomerAccountCandidate>>('GET', `/customers/${customerId}/related-customers?${params}`);
  return { candidates: res.data, hasMore: res.pagination.hasMore, nextCursor: res.pagination.nextCursor };
};

export const updateCustomerAccount = async (customerId: string, accountOperation: CustomerAccountOperation) => {
  const res = await makeApiCall<ItemEnvelope<{ account: CustomerAccountSummary | null }>>('PATCH', `/customers/${customerId}`, { accountOperation });
  return res.data.account;
};

export const getCustomers = async (
  status?: string,
  source?: string,
  pageSize?: number,
  page?: number,
  isActive?: boolean,
  query?: string,
  sortBy?: 'createdAt' | 'name',
  sortOrder?: 'asc' | 'desc',
  pendingRateRequests?: boolean,
  limit?: number,
  cursor?: string,
): Promise<{
  customers: Customer[];
  pagination: {
    hasMore: boolean;
    nextCursor?: string;
  };
}> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (source) params.append('source', source);
  if (pageSize !== undefined) params.append('pageSize', pageSize.toString());
  if (page !== undefined) params.append('page', page.toString());
  if (isActive !== undefined) params.append('isActive', isActive.toString());
  if (query) params.append('query', query);
  if (sortBy) params.append('sortBy', sortBy);
  if (sortOrder) params.append('sortOrder', sortOrder);
  if (pendingRateRequests !== undefined) params.append('pendingRateRequests', pendingRateRequests.toString());
  if (limit !== undefined) params.append('limit', limit.toString());
  if (cursor) params.append('cursor', cursor);

  const queryString = params.toString();
  const res = await makeApiCall<ListEnvelope<Customer>>('GET', `/customers${queryString ? `?${queryString}` : ''}`);
  return {
    customers: res.data,
    pagination: {
      hasMore: res.pagination.hasMore,
      nextCursor: res.pagination.nextCursor ?? undefined,
    },
  };
};

export const getCustomer = async (
  customerId: string,
): Promise<{ customer: Customer; customersAtSameAddress: CustomerAddressNeighbor[]; pendingAccessRequests: PendingAccessRequest[]; account: CustomerAccountSummary | null }> => {
  const res = await makeApiCall<ItemEnvelope<{ customer: Customer; customersAtSameAddress?: CustomerAddressNeighbor[]; pendingAccessRequests?: PendingAccessRequest[]; account?: CustomerAccountSummary | null }>>('GET', `/customers/${customerId}?include=account`);
  return {
    customer: res.data.customer,
    customersAtSameAddress: res.data.customersAtSameAddress ?? [],
    pendingAccessRequests: res.data.pendingAccessRequests ?? [],
    account: res.data.account ?? null,
  };
};

/**
 * Owner's decision on a pending request from a new person to get service at an
 * address that already belongs to this customer (RUT-2228). JOIN links the
 * requester onto this account; SEPARATE spins them off into their own customer
 * carrying only the property details.
 */
export const decideAccessRequest = async (
  customerId: string,
  body: { contactType: 'EMAIL' | 'PHONE'; contactValue: string; decision: AccessRequestDecisionType },
): Promise<{ decision: AccessRequestDecisionType; customerId: string; customer?: Customer }> => {
  const res = await makeApiCall<ItemEnvelope<{ decision: AccessRequestDecisionType; customerId: string; customer?: Customer }>>(
    'PATCH',
    `/customers/${customerId}/access-requests`,
    body,
  );
  return res.data;
};

/**
 * A customer's terms acceptances (newest first), including an acceptance known
 * only from the customer's own date stamp — that one carries
 * `contentSnapshotUnavailable` and has no copy of the agreed text.
 */
export const getCustomerTermsHistory = async (
  customerId: string,
): Promise<{ acceptances: TermsAcceptance[] }> => {
  const res = await makeApiCall<ItemEnvelope<{ acceptances: TermsAcceptance[] }>>(
    'GET',
    `/customers/${customerId}/terms-history`,
  );
  return res.data;
};

/**
 * Other same-org customers that share a contact phone or email with this one
 * (RUT-4359). Loaded separately from the customer page so the profile does
 * not wait on the lookup. Empty when nobody else shares a contact.
 */
export const getRelatedCustomers = async (
  customerId: string,
): Promise<RelatedCustomer[]> => {
  const res = await makeApiCall<ListEnvelope<RelatedCustomer>>(
    'GET',
    `/customers/${customerId}/related-customers`,
  );
  return res.data;
};

/** Full append-only history of a customer's portal logins (newest first). */
export const getCustomerLoginHistory = async (
  customerId: string,
): Promise<{ logins: PortalLogin[] }> => {
  const res = await makeApiCall<ItemEnvelope<{ logins: PortalLogin[] }>>(
    'GET',
    `/customers/${customerId}/logins`,
  );
  return res.data;
};

export interface MapMarker {
  id: string;
  name: string;
  coordinates: [number, number]; // [lng, lat]
  status?: string;
  parcelId?: string;
  address?: string;
  phone?: string;
  email?: string;
  activeServiceCount?: number;
  customerSince?: string;
}

export const getCustomerMapMarkers = async (): Promise<{ markers: MapMarker[]; found: number }> => {
  const res = await makeApiCall<ItemEnvelope<{ markers: MapMarker[]; found: number }>>(
    'GET',
    '/search?type=customers&fields=minimal&fetchAll=true&hasCoordinates=true',
  );
  return res.data;
};

export const getCustomerStats = async (): Promise<{
  active: number;
  inactive: number;
  total: number;
}> => {
  const res = await makeApiCall<ItemEnvelope<{ active: number; inactive: number; total: number }>>('GET', '/customers/stats');
  return res.data;
};

export const createCustomer = async (
  customerData: CustomerFormData,
): Promise<{ customer: Customer }> => {
  const res = await makeApiCall<ItemEnvelope<{ customer: Customer }>>('POST', '/customers', customerData);
  return res.data;
};

export const updateCustomer = async (
  customerId: string,
  customerData: Partial<Customer>,
): Promise<{
  customer: Customer;
}> => {
  const res = await makeApiCall<ItemEnvelope<{ customer: Customer }>>('PUT', `/customers/${customerId}`, customerData);
  return res.data;
};

export const patchCustomer = async (
  customerId: string,
  customerData: Partial<Customer> & {
    allowPartialAddress?: boolean;
    convertToContactOnCustomerId?: string;
    role?: ContactRole;
  },
): Promise<{
  customer: Customer;
  convertToContactOnCustomerId?: string;
}> => {
  const res = await makeApiCall<ItemEnvelope<{ customer: Customer }>>('PATCH', `/customers/${customerId}`, customerData);
  return res.data;
};

export const refreshCustomerTaxRates = async (
  customerId: string,
  options?: { forceRefresh?: boolean },
): Promise<{
  customer: Customer;
}> => {
  const res = await makeApiCall<ItemEnvelope<{ customer: Customer }>>('POST', `/customers/${customerId}/tax-rates`, options || {});
  return res.data;
};

export interface CustomerParcelResult {
  parcelBoundaries?: number[][];
  parcelData?: Customer['parcelData'];
  parcelFetchedAt?: string;
}

/**
 * Fetch and store parcel boundaries + property data for a customer from their
 * geocoded coordinates. Used to generate a starting boundary when one is missing.
 */
export const fetchCustomerParcel = async (
  customerId: string,
  options?: { forceRefresh?: boolean },
): Promise<CustomerParcelResult> => {
  const res = await makeApiCall<ItemEnvelope<CustomerParcelResult>>('POST', `/customers/${customerId}/parcel`, options || {});
  return res.data;
};

// =====================
// Rate Request Management
// =====================

export const generateRateRequest = async (
  customerId: string,
  requestData: {
    serviceTemplateIds: string[];
    notes?: string;
    serviceStartDate?: 'asap' | 'specific';
    specificDate?: string;
  },
): Promise<{
  message: string;
  rateRequest: any;
  rateRequestUrl: string;
}> => {
  return makeApiCall('POST', `/customers/${customerId}/generate-rate-request`, requestData);
};

export const sendRateRequest = async (
  customerId: string,
  requestData: {
    rateRequestId: string;
    method: 'email' | 'sms' | 'both';
    message?: string;
  },
): Promise<{
  message: string;
  sent: { email: boolean; sms: boolean };
  customer: any;
  rateRequest: any;
  errors?: string[];
}> => {
  return makeApiCall('POST', `/customers/${customerId}/send-rate-request`, requestData);
};

export const updateRateRequestPricing = async (
  rateRequestId: string,
  pricingData: {
    items: Array<{
      serviceTemplateId: string;
      serviceName: string;
      pricing: {
        model: 'per-visit' | 'labor-hourly' | 'square-footage' | 'target-hourly';
        amount: number;
        details?: {
          squareFootage?: number;
          estimatedHours?: number;
          description?: string;
        };
      };
      notes?: string;
    }>;
    totalEstimate: number;
    adminNotes?: string;
    expiresAt?: string;
  },
): Promise<{
  message: string;
  rateRequest: any;
  viewingUrl: string;
  customer: any;
}> => {
  return makeApiCall('PUT', `/rate-requests/${rateRequestId}/pricing`, pricingData);
};

export const getRateRequests = async (
  status?: 'pending' | 'pricing_provided' | 'accepted' | 'declined',
  customerId?: string,
  limit?: number,
): Promise<{
  rateRequests: RateRequest[];
  count: number;
  hasMore: boolean;
  filters?: {
    status: string | null;
    customerId: string | null;
  };
}> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (customerId) params.append('customerId', customerId);
  if (limit) params.append('limit', limit.toString());

  const queryString = params.toString();
  return makeApiCall('GET', `/rate-requests${queryString ? `?${queryString}` : ''}`);
};

export const notifyCustomerPricing = async (
  customerId: string,
  requestData: {
    rateRequestId: string;
    method: 'email' | 'sms' | 'both';
    customMessage?: string;
    type: 'pricing';
  },
): Promise<{
  message: string;
  sent: { email: boolean; sms: boolean };
  customer: any;
  rateRequest: any;
  errors?: string[];
}> => {
  return makeApiCall('POST', `/customers/${customerId}/notifications`, { ...requestData, type: 'pricing' });
};

// Enhanced service rate provision function
export const provideServiceRates = async (
  customerId: string,
  serviceRates: Array<{
    serviceTemplateId: string;
    inputs: Record<string, string | number>;
    calculatedRate: number;
    notes?: string;
  }>,
): Promise<{ message: string; rateRequestId: string }> => {
  return makeApiCall('POST', `/customers/${customerId}/provide-rates`, { serviceRates });
};

// Delete is not supported for subscriptions - mark as inactive instead
export const deleteCustomerService = async (
  customerId: string,
  serviceId: string,
): Promise<{
  message: string;
  service: CustomerService;
}> => {
  // Use the pause action to deactivate the subscription
  const result = await patchCustomerSubscription(customerId, serviceId, {
    action: 'pause',
    adminNotes: 'Service marked as inactive via delete operation'
  });
  
  // Transform subscription to service format
  const service: CustomerService = {
    id: result.subscription.id,
    serviceTemplateId: result.subscription.serviceTemplateId,
    serviceTemplateName: result.subscription.serviceTemplateName,
    state: 'inactive',
    parameters: result.subscription.parameters,
    calculatedRate: result.subscription.calculatedRate,
    notes: result.subscription.notes,
    stateHistory: [],
    createdAt: result.subscription.createdAt,
    updatedAt: result.subscription.updatedAt,
  };
  
  return {
    message: 'Service marked as inactive',
    service,
  };
};

// =====================
// Customer Rates API
// =====================

export const getCustomerRates = async (
  customerId: string,
): Promise<Array<{
  rateId: string;
  customerId: string;
  serviceTemplateId: string;
  serviceName: string;
  baseRate: number;
  parameters: Record<string, number>;
  breakdown: any[];
  notes?: string;
  validUntil: string;
  createdAt: string;
  updatedAt: string;
}>> => {
  return makeApiCall('GET', `/customers/${customerId}/rates`);
};

export const saveCustomerRate = async (
  customerId: string,
  rateData: {
    serviceTemplateId: string;
    serviceName: string;
    parameters: Record<string, number>;
    baseRate: number;
    breakdown: any[];
    notes?: string;
    validUntil?: string;
  },
): Promise<{ message: string; rateId: string }> => {
  return makeApiCall('POST', `/customers/${customerId}/rates`, rateData);
};

export const updateCustomerRate = async (
  customerId: string,
  rateId: string,
  rateData: {
    serviceTemplateId: string;
    serviceName: string;
    parameters: Record<string, number>;
    baseRate: number;
    breakdown: any[];
    notes?: string;
    validUntil?: string;
  },
): Promise<{ message: string; rateId: string }> => {
  return makeApiCall('PUT', `/customers/${customerId}/rates/${rateId}`, rateData);
};

// =====================
// Customer Subscriptions API
// =====================

export const getCustomerSubscriptions = async (
  customerId: string,
): Promise<Pick<CustomerSubscriptionsResponse, 'subscriptions' | 'summary'>> => {
  const res = await makeApiCall<ItemEnvelope<Pick<CustomerSubscriptionsResponse, 'subscriptions' | 'summary'>>>(
    'GET',
    `/customers/${customerId}/subscriptions`,
  );
  return res.data;
};

export const patchCustomerSubscription = async (
  customerId: string,
  serviceTemplateId: string,
  updateData: {
    adminNotes?: string;
    adminAttachments?: Array<{
      url: string;
      fileName: string;
      mimeType: string;
    }>;
    action?: 'accept' | 'reject' | 'activate' | 'pause' | 'update_pause' | 'cancel' | 'dismiss' | 'cancel_deactivation' | 'cancel_activation' | 'reschedule_activation' | 'make_visible' | 'make_invisible' | 'skip_period' | 'unskip_period' | 'finalize_plan_change' | 'decline_plan_change' | 'start_plan_change' | 'refund_package_charge' | 'clear_next_visit';
    date?: string; // YYYY-MM-DD format for scheduled cancellation
    startDate?: string; // YYYY-MM-DD format for deferred activation (RUT-2910)
    cancellationReason?: string;
    // Structured deactivation reasons (RUT-1368) — selected DEACTIVATION_REASON picklist
    // labels, with "Custom" replaced by the typed free text.
    deactivationReasons?: string[];
    /** Crew note for remaining visits until a scheduled stop (RUT-2773). */
    deactivationCrewNote?: string | null;
    /** Skip make-up flag (RUT-2923): next visit gets a note + review + optional hourly billing. Null clears it. */
    skipMakeup?: SkipMakeupFlag | null;
    notifyCustomer?: boolean;
    customerMessage?: string;
    /** Skip the customer plan-changed notification (RUT-4543). */
    skipMessage?: boolean;
    /** Paid-season plan switch: apply the refund/charge, or switch the plan only. */
    moneyChoice?: 'apply' | 'defer';
    /** Auto-resume date for pause / update_pause. null clears it on update_pause. */
    pausedUntil?: string | null;
    preferredDayOfWeek?: number;
    preferredDayOfMonth?: number;
    preferredMonth?: number;
    preferredDayOfYear?: number;
    firstVisitDate?: string;
    assignedCrewId?: string;
    order?: number;
    showAttachmentsToCrew?: boolean;
    customerNotes?: string | null;
    customerNotesReleasedToCrew?: boolean;
    /** Crew-facing notes that expire on their own (RUT-1950). Full-array replace. */
    temporaryNotes?: TemporaryNote[];
    /** Service-level banner notes (RUT-3570). Same TemporaryNote shape; full-array replace. */
    bannerNotes?: TemporaryNote[];
    // Per-period crew-note overrides (RUT-2615). Full-set replace; empty clears all.
    periodNotes?: Array<{ periodId: string; note: string; note_es?: string }>;
    packageRefund?: {
      type: 'full' | 'credit' | 'partial' | 'none';
      amount?: number;
    };
    /** Deactivate now with an unpaid season invoice: keep owed or void it. */
    unpaidPackageInvoice?: 'keep' | 'void';
    selectedPeriodIds?: string[];
    /**
     * RUT-1828: define/replace/clear the inline season(s) on a custom (one-off)
     * service. Non-empty ties the open recurring schedule to the season; an
     * empty array clears it (the service runs year-round again).
     */
    seasonalAvailability?: Period[];
    customerSelectedOptions?: Array<{ optionId: string; enabled: boolean; choiceId?: string }>;
    /**
     * Per-customer service-option review-threshold overrides keyed by option id.
     * A number sets/replaces the override; `null` clears it (falls back to the
     * template default). Options omitted from the map are left untouched.
     */
    serviceOptionThresholds?: Record<string, number | null>;
    /**
     * Arm proof of service on this one customer's service: the next visit needs
     * before and after photos and is held for review. `false` disarms it.
     */
    armProofOfService?: boolean;
    /**
     * RUT-1826: per-company subcontractor income-% overrides for a custom
     * (one-off) service. Empty array clears all overrides.
     */
    subcontractorRateOverrides?: SubcontractorRateOverride[];
    /** Activation cadence: true → limited (one-time) schedule, false → recurring. */
    isOneTime?: boolean;
    rateMessage?: string;
    completionMessage?: string;
    invoiceNotes?: string | null;
    /**
     * Direct rate override — stored as-is without recomputing from pricingInputs.
     * `null` unsets it; the API allows that only on a service that isn't active (RUT-4435).
     */
    rate?: number | null;
    /** Direct per-additional-visit price override (package services). */
    pricePerAdditionalVisit?: number;
    /** Per-option package totals + per-visit rates (mirrors the selected option to rate). */
    packageOptionPricing?: import('@ruta/shared').PackageOptionPricing[];
    /** One-time setup fee, charged at activation. `null` removes the configured fee. */
    setupFee?: number | null;
    /**
     * Ongoing service discount applied to every visit (RUT-4219). `null` removes it.
     * `expiresAt` is YYYY-MM-DD.
     */
    discount?: {
      type: 'percentage' | 'fixed';
      value: number;
      reason?: string;
      expiresAt?: string;
    } | null;
    /** Target package option when starting a plan switch (RUT-3990). */
    selectedPackageOptionId?: string;
    /**
     * Per-schedule edit. When set, mutates a single entry in
     * `Subscription.schedules[]`. Mutually exclusive with `action` /
     * sub-level edits in this same request.
     */
    scheduleEdit?: {
      scheduleId: string;
      periodIds?: string[]; // omit to leave unchanged; empty array clears
      deactivate?: boolean; // sets deactivatedAt + fulfillment.status='expired'
      notifyCustomer?: boolean; // SERVICE_CANCELLED notification when last schedule closes
      customerMessage?: string; // optional admin-authored note appended to the notification
      frequency?: ScheduleFrequency; // override cadence for this customer's service
      customConfig?: CustomScheduleConfig | null; // required when frequency is CUSTOM
    };
    /**
     * Route template periods to the recurring and/or limited schedule at once.
     * For each cadence present: non-empty ids set (or create) that schedule's
     * periods; an empty array deactivates that cadence's open schedule; omitting
     * a cadence leaves it untouched. Mutually exclusive with sub-level edits.
     */
    periodScheduleAssignment?: {
      recurring?: string[];
      limited?: string[];
    };
  }
): Promise<{ message: string; subscription: any }> => {
  const res = await makeApiCall<ItemEnvelope<{ message: string; subscription: any }>>(
    'PATCH',
    `/customers/${customerId}/subscriptions/${serviceTemplateId}`,
    updateData
  );
  return res.data;
};

/** Refund preview for a season package charge (RUT-4049). Moves nothing. */
export interface PackageChargeRefundPreview {
  paymentId: string;
  chargedAt: string;
  chargedAmount: number;
  refundedTotal: number;
  refundable: number;
  card: { brand?: string; last4?: string } | null;
  /** RUT-4188: a Full refund also sends back plan-upgrade payments. */
  fullRefund?: {
    total: number;
    paymentCount: number;
    payments: Array<{ kind: 'season' | 'extra'; amount: number; reason?: string }>;
  };
}

export interface PackageChargeRefundOutcome {
  amount: number;
  refundId?: string;
  wasVoided: boolean;
  fullyRefunded: boolean;
  refundedTotal: number;
  chargedAmount: number;
  paymentId: string;
  /** RUT-4188: one row per payment when more than one went back. */
  payments?: Array<{ kind: 'season' | 'extra'; paymentId: string; amount: number; refundId?: string }>;
}

export const previewPackageChargeRefund = async (
  customerId: string,
  serviceTemplateId: string,
): Promise<PackageChargeRefundPreview> => {
  const res = await makeApiCall<ItemEnvelope<{ preview: PackageChargeRefundPreview }>>(
    'PATCH',
    `/customers/${customerId}/subscriptions/${serviceTemplateId}`,
    { action: 'refund_package_charge', packageRefund: { preview: true } },
  );
  return res.data.preview;
};

export const refundPackageCharge = async (
  customerId: string,
  serviceTemplateId: string,
  refund: { type: 'full' } | { type: 'partial'; amount: number },
): Promise<{ message: string; subscription: any; packageRefund: PackageChargeRefundOutcome }> => {
  const res = await makeApiCall<
    ItemEnvelope<{ message: string; subscription: any; packageRefund: PackageChargeRefundOutcome }>
  >('PATCH', `/customers/${customerId}/subscriptions/${serviceTemplateId}`, {
    action: 'refund_package_charge',
    packageRefund: refund,
  });
  return res.data;
};


export const provideSubscriptionRate = async (
  customerId: string,
  serviceTemplateId: string,
  rateData: {
    rate?: number;
    pricePerAdditionalVisit?: number;
    packageOptionPricing?: import('@ruta/shared').PackageOptionPricing[];
    rateVisibility?: import('@ruta/shared').RateVisibility;
    pricingInputs?: any;
    parameters?: Record<string, number>;
    hourBounds?: {
      upperBound: number;
      lowerBound?: number;
    };
    adminNotificationConfig?: import('@ruta/shared').AdminNotificationConfig;
    visitReviewRequirements?: import('@ruta/shared').VisitReviewRequirements;
    minimumCharge?: number;
    builtInHours?: number;
    adminNotes?: string;
    action?: import('@ruta/shared').SubscriptionAction;
    message?: string;
    skipMessage?: boolean;
    firstVisitDate?: string;
    assignedCrewId?: string;
    selectedPackageOptionId?: string;
    selectedPeriodIds?: string[];
    discount?: { type: 'percentage' | 'fixed'; value: number; reason?: string } | null;
  },
): Promise<{ message: string; subscription: any }> => {
  // Always use PATCH - handles both rate provision and protection settings
  const res = await makeApiCall<ItemEnvelope<{ message: string; subscription: any }>>(
    'PATCH',
    `/customers/${customerId}/subscriptions/${serviceTemplateId}`,
    rateData,
  );
  return res.data;
};

/**
 * Apply a measurement the operator just corrected on one service to the
 * customer's OTHER services that price off the same measurement (RUT-3357).
 *
 * Reprices those services through the same fan-out an assessment uses, and
 * records the measurement on the customer, so it is only ever called after the
 * operator confirms the offer. `serviceTemplateId` is the service that was
 * edited — it is excluded from the fan-out. Pass only the measurements that
 * actually changed; an omitted one is left untouched.
 *
 * `visitPropagationFailures` > 0 means a service moved to its new rate but the
 * visits already on its calendar did not — tell the operator, don't swallow it.
 */
export const applyMetricsToOtherServices = async (
  customerId: string,
  serviceTemplateId: string,
  metrics: import('@ruta/shared').CustomerMetrics,
): Promise<{ message: string; updatedOtherServices: number; visitPropagationFailures: number }> => {
  const res = await makeApiCall<ItemEnvelope<{ message: string; updatedOtherServices: number; visitPropagationFailures: number }>>(
    'PATCH',
    `/customers/${customerId}/subscriptions/${serviceTemplateId}`,
    { applyMetricsToOtherServices: metrics },
  );
  return res.data;
};

export const acceptCustomerSubscriptionRate = async (
  customerId: string,
  serviceTemplateId: string,
  acceptanceData: {
    action: 'accept' | 'reject';
    adminNotes?: string;
  },
): Promise<{ message: string; subscription: any }> => {
  const res = await makeApiCall<ItemEnvelope<{ message: string; subscription: any }>>(
    'PATCH',
    `/customers/${customerId}/subscriptions/${serviceTemplateId}`,
    acceptanceData,
  );
  return res.data;
};

export const getCustomerSubscription = async (
  customerId: string,
  subscriptionId: string,
): Promise<{
  subscription: any;
  noteAcknowledgments?: any;
  deactivationAcknowledgments?: Array<{ memberName: string; acknowledgedAt: string }>;
}> => {
  const res = await makeApiCall<ItemEnvelope<{
    subscription: any;
    noteAcknowledgments?: any;
    deactivationAcknowledgments?: Array<{ memberName: string; acknowledgedAt: string }>;
  }>>(
    'GET',
    `/customers/${customerId}/subscriptions/${subscriptionId}`,
  );
  return res.data;
};

export const activateCustomerSubscription = async (
  customerId: string,
  serviceTemplateId: string,
  adminNotes?: string,
  selectedPackageOptionId?: string,
  isOneTime?: boolean,
  customerSelectedOptions?: Array<{ optionId: string; enabled: boolean; choiceId?: string }>,
  selectedPeriodIds?: string[],
): Promise<{ message: string; subscription: any }> => {
  const res = await makeApiCall<ItemEnvelope<{ message: string; subscription: any }>>(
    'PATCH',
    `/customers/${customerId}/subscriptions/${serviceTemplateId}`,
    {
      action: 'activate',
      adminNotes,
      selectedPackageOptionId,
      isOneTime,
      customerSelectedOptions,
      selectedPeriodIds,
    },
  );
  return res.data;
};

export const pauseCustomerSubscription = async (
  customerId: string,
  serviceTemplateId: string,
  adminNotes?: string,
  pausedUntil?: string,
  notifyCustomer?: boolean,
  customerMessage?: string,
): Promise<{ message: string; subscription: any }> => {
  const res = await makeApiCall<ItemEnvelope<{ message: string; subscription: any }>>(
    'PATCH',
    `/customers/${customerId}/subscriptions/${serviceTemplateId}`,
    {
      action: 'pause',
      adminNotes,
      pausedUntil,
      notifyCustomer,
      customerMessage,
    },
  );
  return res.data;
};

/** Change resume date / reason on an already-paused service (RUT-3956). */
export const updatePausedCustomerSubscription = async (
  customerId: string,
  serviceTemplateId: string,
  data: {
    pausedUntil?: string | null;
    adminNotes?: string;
  },
): Promise<{ message: string; subscription: any }> => {
  const res = await makeApiCall<ItemEnvelope<{ message: string; subscription: any }>>(
    'PATCH',
    `/customers/${customerId}/subscriptions/${serviceTemplateId}`,
    {
      action: 'update_pause',
      pausedUntil: data.pausedUntil,
      adminNotes: data.adminNotes,
    },
  );
  return res.data;
};

export const releasePendingRates = async (
  customerId: string,
  data: {
    subscriptionIds: string[];
    message?: string;
    skipMessage?: boolean;
  },
): Promise<{ message: string; updatedSubscriptions: any[] }> => {
  // Updated to use RESTful PATCH endpoint for batch subscription updates with dual-field model
  const res = await makeApiCall<ItemEnvelope<{ message: string; updatedSubscriptions: any[] }>>(
    'PATCH',
    `/customers/${customerId}/subscriptions`,
    {
      subscriptionIds: data.subscriptionIds,
      updates: {
        rateVisibility: RateVisibility.VISIBLE
      },
      message: data.message,
      skipMessage: data.skipMessage
    },
  );
  return res.data;
};

export const hideRates = async (
  customerId: string,
  data: {
    subscriptionIds: string[];
  },
): Promise<{ message: string; updatedSubscriptions: any[] }> => {
  const res = await makeApiCall<ItemEnvelope<{ message: string; updatedSubscriptions: any[] }>>(
    'PATCH',
    `/customers/${customerId}/subscriptions`,
    {
      subscriptionIds: data.subscriptionIds,
      updates: {
        rateVisibility: RateVisibility.INVISIBLE
      },
      skipMessage: true
    },
  );
  return res.data;
};

export const cancelAllCustomerServices = async (
  customerId: string,
  data: {
    reason: string;
    date?: string;
    subscriptionIds: string[]; // Now required - passed from the component
    skipMessage?: boolean;
    message?: string;
    deactivationReasons?: string[];
    /** Crew note for remaining visits until a scheduled stop (RUT-2773). */
    deactivationCrewNote?: string | null;
  },
): Promise<{ message: string; updatedSubscriptions: any[] }> => {
  const res = await makeApiCall<ItemEnvelope<{ message: string; updatedSubscriptions: any[] }>>(
    'PATCH',
    `/customers/${customerId}/subscriptions`,
    {
      subscriptionIds: data.subscriptionIds,
      action: 'cancel',
      cancellationReason: data.reason,
      cancellationDate: data.date,
      deactivationReasons: data.deactivationReasons,
      deactivationCrewNote: data.deactivationCrewNote,
      skipMessage: data.skipMessage,
      message: data.message,
      updates: {},
    },
  );
  return res.data;
};

// =====================
// Customer Visits API
// =====================

export interface VisitSummary {
  id: string;
  createdAt: string;
  completedAt?: string;
  status: string;
  hasNotes: boolean;
  hasPhotos: boolean;
}

export const getCustomerVisits = async (
  customerId: string,
  params?: {
    status?: string;
    serviceTemplateId?: string;
    upcoming?: boolean;
    limit?: number;
    summaryOnly?: boolean;
    sort?: 'asc' | 'desc';
    cursor?: string;
  }
): Promise<{ visits: any[]; nextCursor: string | null; hasMore: boolean }> => {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append('status', params.status);
  if (params?.serviceTemplateId) queryParams.append('serviceTemplateId', params.serviceTemplateId);
  if (params?.upcoming) queryParams.append('upcoming', 'true');
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.sort) queryParams.append('sort', params.sort);
  if (params?.cursor) queryParams.append('cursor', params.cursor);

  const queryString = queryParams.toString();
  const url = `/customers/${customerId}/visits${queryString ? `?${queryString}` : ''}`;

  const headers = params?.summaryOnly ? { 'X-Summary-Only': 'true' } : undefined;
  const res = await makeApiCall<ListEnvelope<any>>('GET', url, undefined, headers);
  return {
    visits: res.data,
    nextCursor: res.pagination?.nextCursor ?? null,
    hasMore: res.pagination?.hasMore ?? false,
  };
};

// =====================
// Customer Emails API (Admin)
// =====================

export interface CustomerEmail {
  id: string;
  email: string;
  isPrimary: boolean;
  isVerified: boolean;
  verifiedAt?: string;
  firstName: string;
  lastName?: string;
  role: import('@ruta/shared').ContactRole;
  createdAt: string;
  updatedAt?: string;
}

export interface CustomerEmailsResponse {
  emails: CustomerEmail[];
  primaryEmail: {
    id: string;
    email: string;
    isVerified: boolean;
  } | null;
  totalEmails: number;
  verifiedEmails: number;
  unverifiedEmails: number;
}

export const getCustomerEmails = async (
  customerId: string,
): Promise<CustomerEmailsResponse> => {
  const res = await makeApiCall<ItemEnvelope<CustomerEmailsResponse>>('GET', `/customers/${customerId}/emails`);
  return res.data;
};

export const addCustomerEmail = async (
  customerId: string,
  data: {
    email: string;
    firstName: string;
    lastName?: string;
    role?: import('@ruta/shared').ContactRole;
    isPrimary?: boolean;
  },
): Promise<CustomerEmail> => {
  const res = await makeApiCall<ItemEnvelope<CustomerEmail>>('POST', `/customers/${customerId}/emails`, data);
  return res.data;
};

export const deleteCustomerEmail = async (
  customerId: string,
  email: string,
): Promise<{ email: string }> => {
  const res = await makeApiCall<ItemEnvelope<{ email: string }>>('DELETE', `/customers/${customerId}/emails/${encodeURIComponent(email)}`);
  return res.data;
};

export interface ContactOwnerUpdate {
  contactType: 'email' | 'phone';
  contactValue: string;
  firstName: string;
  lastName?: string;
  role?: import('@ruta/shared').ContactRole;
}

/**
 * Rename a contact, or move contact methods to a different person on the
 * customer. Goes through the existing PATCH /customers/{id} endpoint (no
 * dedicated contact route); each update is applied in-place so verification and
 * notification status are preserved. Pass multiple updates to rename every
 * method a person owns in one request.
 */
export const updateCustomerContactOwner = async (
  customerId: string,
  updates: ContactOwnerUpdate[],
): Promise<void> => {
  await makeApiCall<ItemEnvelope<{ customer: unknown }>>('PATCH', `/customers/${customerId}`, {
    contactOperations: updates.map((u) => ({ action: 'update-owner', ...u })),
  });
};

// =====================
// Customer Phones API (Admin)
// =====================

export interface CustomerPhone {
  id: string;
  phone: string;
  isPrimary: boolean;
  isVerified: boolean;
  verifiedAt?: string;
  firstName: string;
  lastName?: string;
  role: import('@ruta/shared').ContactRole;
  phoneType?: import('@ruta/shared').PhoneType;
  createdAt: string;
  updatedAt?: string;
  smsOptedOut?: boolean;
  smsOptedOutAt?: string;
}

export interface CustomerPhonesResponse {
  phones: CustomerPhone[];
  primaryPhone: {
    id: string;
    phone: string;
    isVerified: boolean;
  } | null;
  totalPhones: number;
  verifiedPhones: number;
  unverifiedPhones: number;
}

export const getCustomerPhones = async (
  customerId: string,
): Promise<CustomerPhonesResponse> => {
  const res = await makeApiCall<ItemEnvelope<CustomerPhonesResponse>>('GET', `/customers/${customerId}/phones`);
  return res.data;
};

export const addCustomerPhone = async (
  customerId: string,
  data: {
    phone: string;
    firstName: string;
    lastName?: string;
    role?: import('@ruta/shared').ContactRole;
    isPrimary?: boolean;
    phoneType?: import('@ruta/shared').PhoneType;
  },
): Promise<CustomerPhone> => {
  const res = await makeApiCall<ItemEnvelope<CustomerPhone>>('POST', `/customers/${customerId}/phones`, data);
  return res.data;
};

export const updateCustomerPhoneType = async (
  customerId: string,
  phone: string,
  phoneType: import('@ruta/shared').PhoneType,
): Promise<{ phone: string; phoneType: import('@ruta/shared').PhoneType }> => {
  const res = await makeApiCall<ItemEnvelope<{ phone: string; phoneType: import('@ruta/shared').PhoneType }>>('PATCH', `/customers/${customerId}/contacts/primary`, { contactType: 'phone', contactValue: phone, phoneType });
  return res.data;
};

export const deleteCustomerPhone = async (
  customerId: string,
  phone: string,
): Promise<{ phone: string }> => {
  const res = await makeApiCall<ItemEnvelope<{ phone: string }>>('DELETE', `/customers/${customerId}/phones/${encodeURIComponent(phone)}`);
  return res.data;
};

// =====================
// Unified Primary Contact API
// =====================

export interface TogglePrimaryContactResponse {
  toggledContact: {
    contactType: 'EMAIL' | 'PHONE';
    contactValue: string;
    isPrimary: boolean;
  };
  emails: CustomerEmail[];
  phones: CustomerPhone[];
}

/**
 * Toggle a contact's primary status. Multiple contacts can be primary simultaneously.
 */
export const toggleCustomerPrimaryContact = async (
  customerId: string,
  contactType: 'email' | 'phone',
  contactValue: string,
): Promise<TogglePrimaryContactResponse> => {
  const res = await makeApiCall<ItemEnvelope<TogglePrimaryContactResponse>>('PATCH', `/customers/${customerId}/contacts/primary`, {
    contactType,
    contactValue,
  });
  return res.data;
};

// =====================
// Contact Lookup API (Multi-Property Support)
// =====================

export interface LinkedCustomer {
  customerId: string;
  orgId: string;
  name: string;
  address?: string;
}

/**
 * Look up all customers linked to a contact (email or phone).
 * Used to show multi-property relationships in admin UI.
 * Unwraps the standard list envelope and returns the customers directly.
 */
export const lookupContactCustomers = async (
  contactValue: string,
): Promise<LinkedCustomer[]> => {
  const res = await makeApiCall<{ data: LinkedCustomer[] }>(
    'GET',
    `/contacts/lookup?value=${encodeURIComponent(contactValue)}`,
  );
  return res.data;
};

export interface EmailOperation {
  action: 'add' | 'remove' | 'verify' | 'set-primary';
  email: string;
  isPrimary?: boolean;
  sendVerification?: boolean;
  verificationCode?: string;
}

export const updateCustomerWithEmails = async (
  customerId: string,
  customerData: Partial<Customer> & { emailOperations?: EmailOperation[] },
): Promise<{
  customer: Customer;
  emails?: CustomerEmail[];
  warnings?: { emails: any[] };
}> => {
  const res = await makeApiCall<ItemEnvelope<{
    customer: Customer;
    emails?: CustomerEmail[];
    warnings?: { emails: any[] };
  }>>('PATCH', `/customers/${customerId}`, customerData);
  return res.data;
};

// =====================
// Welcome Emails API
// =====================

export interface SendWelcomeEmailsResponse {
  success: boolean;
  emailsSent: number;
  errors?: string[];
}

export const sendWelcomeEmails = async (): Promise<SendWelcomeEmailsResponse> => {
  const res = await makeApiCall<ItemEnvelope<SendWelcomeEmailsResponse>>('POST', '/customers/welcome-emails');
  return res.data;
};

// =====================
// Customer Aerial Imagery API
// =====================

export interface AerialImageryUrls {
  // Always available (free, no API key)
  arcgis: string;

  // Requires API keys (optional)
  google?: string;
  mapbox?: string;

  // MnGeo layers (free, Minnesota high-resolution)
  mngeo_fsa2023: string;
  mngeo_fsa2021: string;
  mngeo_met20: string;
  mngeo_hen22: string;
  mngeo_hen18: string;
  mngeo_dak23: string;
}

export const getCustomerImagery = async (
  customerId: string,
): Promise<AerialImageryUrls> => {
  const res = await makeApiCall<ItemEnvelope<AerialImageryUrls>>('GET', `/customers/${customerId}/imagery`);
  return res.data;
};

// =====================
// Auto-Segmentation API
// =====================

export type ImagerySource =
  | 'mngeo_fsa2023'
  | 'mngeo_fsa2021'
  | 'mngeo_met20'
  | 'mngeo_hen22'
  | 'mngeo_hen18'
  | 'mngeo_dak23'
  | 'arcgis'
  | 'google'
  | 'mapbox'
  | 'naip_ndvi';

export interface ProposedMeasurement {
  type: string; // CustomerMetricType (e.g., 'turf', 'rock', 'wood_mulch')
  polygon: [number, number][]; // Array of [lng, lat] coordinates
  sqft: number;
  name?: string;
}

export interface AutoSegmentRequest {
  imagerySource: ImagerySource;
}

export interface AutoSegmentResponse {
  proposals: ProposedMeasurement[];
  imageryUsed: ImagerySource;
  imageryLabel: string;
  processingTimeMs: number;
  parcelBoundariesUpdated: boolean;
}

// =====================
// Customer Payment Methods API (Admin)
// =====================

export interface CustomerPaymentMethod {
  id: string;
  type: 'card' | 'bank_account';
  last4: string;
  isDefault: boolean;
  expiryMonth?: number;
  expiryYear?: number;
  brand?: string;
  bankName?: string;
  accountType?: string;
  contactId?: string;
  createdAt?: string;
}

export interface CustomerPaymentMethodsResponse {
  paymentMethods: CustomerPaymentMethod[];
  webComponentToken?: string;
  accountId?: string;
  achPaymentsEnabled?: boolean;
}

export const getCustomerPaymentMethods = async (
  customerId: string,
): Promise<CustomerPaymentMethodsResponse> => {
  const res = await makeApiCall<ItemEnvelope<CustomerPaymentMethodsResponse>>('GET', `/customers/${customerId}/payment-methods`);
  return res.data;
};

export const createCustomerPaymentMethod = async (
  customerId: string,
  data: {
    paymentMethodToken: string;
    type: 'card' | 'bank_account';
    last4?: string;
    brand?: string;
    expMonth?: string;
    expYear?: string;
    bankName?: string;
    contactId?: string;
  },
): Promise<{ paymentMethodId: string; type: string; isDefault: boolean; last4?: string; brand?: string }> => {
  const res = await makeApiCall<ItemEnvelope<{ paymentMethodId: string; type: string; isDefault: boolean; last4?: string; brand?: string }>>('POST', `/customers/${customerId}/payment-methods`, data);
  return res.data;
};

export const setCustomerDefaultPaymentMethod = async (
  customerId: string,
  paymentMethodId: string,
): Promise<{ id: string; isDefault: boolean; updatedAt: string }> => {
  const res = await makeApiCall<ItemEnvelope<{ id: string; isDefault: boolean; updatedAt: string }>>('PATCH', `/customers/${customerId}/payment-methods/${paymentMethodId}/default`);
  return res.data;
};

export const deleteCustomerPaymentMethod = async (
  customerId: string,
  paymentMethodId: string,
): Promise<{ deletedPaymentMethodId: string }> => {
  const res = await makeApiCall<ItemEnvelope<{ deletedPaymentMethodId: string }>>('DELETE', `/customers/${customerId}/payment-methods/${paymentMethodId}`);
  return res.data;
};

// =====================
// Customer Measurements API
// =====================

/**
 * Generate measurement proposals using AI segmentation
 * Returns proposed measurements for user review (not persisted until accepted)
 */
export const postCustomerMeasurementProposals = async (
  customerId: string,
  imagerySource: ImagerySource,
): Promise<AutoSegmentResponse> => {
  const res = await makeApiCall<ItemEnvelope<AutoSegmentResponse>>('POST', `/customers/${customerId}/measurements/proposals`, {
    imagerySource,
  });
  return res.data;
};
